// IR-5 validation for the Order Invite text editors (PR 1.3). Pure module, no
// IO — unit-tested from src/test/inviteValidation.test.ts.
//
// IR-5, precisely:
//  - the INVITE text must contain the STOP opt-out line and identify the
//    business (the {location_name} placeholder, which renders the store name,
//    or the store name typed literally);
//  - the WELCOME text must carry the compliance footer (the {compliance_footer}
//    placeholder, which renders the welcome_footer disclosure — "up to 2
//    msgs/wk" since PR 0.2b — or that disclosure typed literally) and should
//    carry the {offer_link} placeholder for the welcome offer.
//
// A missing compliance element is an "error" (Save is blocked); the welcome
// offer link is a "warning" (the welcome still sends without it, and the
// offer-in-welcome render path is wired by a later PR), so it never blocks.

export type IssueSeverity = "error" | "warning";

export interface ValidationIssue {
  code: string;
  severity: IssueSeverity;
  message: string;
}

export interface ValidationResult {
  issues: ValidationIssue[];
  /** True when nothing blocks the save (no "error" issues). Warnings still allow it. */
  ok: boolean;
}

const STOP_RE = /\bstop\b/i;

function result(issues: ValidationIssue[]): ValidationResult {
  return { issues, ok: !issues.some((i) => i.severity === "error") };
}

function containsLiteral(body: string, literal: string | null | undefined): boolean {
  if (!literal) return false;
  const trimmed = literal.trim();
  if (trimmed.length < 3) return false;
  return body.toLowerCase().includes(trimmed.toLowerCase());
}

/** Validates the invite text against IR-5 (STOP line + business identity). */
export function validateInviteText(body: string, storeName?: string | null): ValidationResult {
  const issues: ValidationIssue[] = [];
  const text = body ?? "";

  if (text.trim() === "") {
    issues.push({ code: "empty", severity: "error", message: "The invite text can't be empty." });
    return result(issues);
  }
  if (!STOP_RE.test(text)) {
    issues.push({
      code: "missing_stop",
      severity: "error",
      message: "Add a STOP line so customers can opt out (e.g. “Reply STOP to opt out”).",
    });
  }
  const identifiesBusiness = text.includes("{location_name}") || containsLiteral(text, storeName);
  if (!identifiesBusiness) {
    issues.push({
      code: "missing_identity",
      severity: "error",
      message: "Name the business — include {location_name} or type your store name.",
    });
  }
  return result(issues);
}

/** Validates the welcome text against IR-5 (compliance footer + offer link). */
export function validateWelcomeText(body: string, welcomeFooter?: string | null): ValidationResult {
  const issues: ValidationIssue[] = [];
  const text = body ?? "";

  if (text.trim() === "") {
    issues.push({ code: "empty", severity: "error", message: "The welcome text can't be empty." });
    return result(issues);
  }
  const hasFooter = text.includes("{compliance_footer}") || containsLiteral(text, welcomeFooter);
  if (!hasFooter) {
    issues.push({
      code: "missing_footer",
      severity: "error",
      message:
        "Add the compliance footer — include {compliance_footer} so the STOP and “up to 2 msgs/wk” terms are attached.",
    });
  }
  if (!text.includes("{offer_link}")) {
    issues.push({
      code: "missing_offer_link",
      severity: "warning",
      message: "Include {offer_link} to send the welcome offer’s link when a welcome offer is set.",
    });
  }
  return result(issues);
}
